import React, { useEffect, useRef, useState } from 'react'

/** Shared observer hook: flips to true once the node scrolls into view. */
export const useInView = (options = {}) => {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -80px 0px', ...options }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [options.threshold, options.rootMargin]) // eslint-disable-line react-hooks/exhaustive-deps

  return [ref, inView]
}

const Reveal = ({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) => {
  const [ref, inView] = useInView()
  return (
    <Tag
      ref={ref}
      style={{ '--reveal-delay': `${delay}ms` }}
      className={`reveal ${inView ? 'is-visible' : ''} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  )
}

/**
 * Display heading whose lines wipe up from behind a mask, staggered.
 * `lines` is an array of nodes — one per visual line, so line breaks stay
 * deliberate at large display sizes instead of wrapping arbitrarily.
 */
export const MaskLines = ({ lines, className = '', stagger = 90, delay = 0, as: Tag = 'h2' }) => {
  const [ref, inView] = useInView()
  return (
    <Tag ref={ref} className={`${inView ? 'is-visible' : ''} ${className}`}>
      {lines.map((line, i) => (
        <span className="line-mask" key={i}>
          <span style={{ '--line-delay': `${delay + i * stagger}ms` }}>{line}</span>
        </span>
      ))}
    </Tag>
  )
}

export default Reveal
